import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { EVENT_SCOPES } from '../events/labels';
import { systemGroups, systemLabel, useSystems } from '../settings/systems';
import { ConflictNotice } from '../ui/ConflictNotice';
import { QuickPicker, type PickerOption } from '../ui/QuickPicker';
import { CellInput, SheetAddRow, SheetSection } from '../ui/Sheet';
import { LinesSheet, sheetSpan } from './LinesSheet';
import { newKey, TEMPLATES_KEY, TEMPLATES_RESOURCE, toDraft, toWire, validQuantities, type BlockSummary, type DraftLine, type Template } from './types';
import './templates.css';
import { leaveGuarded, sameValues, useUnsavedGuard, validForm } from '../ui/unsaved';
import { Notice } from '../ui/Notice';

type DraftBlock = { key: number; blockId: string; name: string; multiplier: string };
type DraftCheck = { key: number; text: string };
type BlockOption = PickerOption & { block: BlockSummary };

const scopeKeys = Object.keys(EVENT_SCOPES) as (keyof typeof EVENT_SCOPES)[];

function TemplateForm({ initial, onClose }: { initial?: Template; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { me } = useMe();
  const [base, setBase] = useState(initial);
  const canEdit = !initial || (me !== undefined && can(me, TEMPLATES_RESOURCE, 'edit'));
  // fields the role may not see are absent from the template: they are neither shown nor sent
  const sees = (field: keyof Template) => !initial || initial[field] !== undefined;
  const [name, setName] = useState(initial?.name ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [scopes, setScopes] = useState<string[]>(initial?.scopes ?? []);
  const [systemId, setSystemId] = useState<string>(initial?.systemId ?? '');
  const systems = useSystems().data?.systems ?? [];
  const [blocks, setBlocks] = useState<DraftBlock[]>((initial?.blocks ?? []).map((b) => ({ key: newKey(), blockId: b.blockId, name: b.name, multiplier: String(b.multiplier) })));
  const [lines, setLines] = useState<DraftLine[]>((initial?.lines ?? []).map(toDraft));
  const [checklist, setChecklist] = useState<DraftCheck[]>((initial?.checklist ?? []).map((text) => ({ key: newKey(), text })));
  const [newCheck, setNewCheck] = useState('');
  const [localError, setLocalError] = useState('');
  const [saved, setSaved] = useState(false);
  // the settings under ⚙ (owner remark 4/32: the sheet first, like the quote); open at once for a new template
  const [showSettings, setShowSettings] = useState(!initial);

  const load = (t: Template) => {
    setBase(t);
    setName(t.name);
    setNotes(t.notes ?? '');
    setScopes(t.scopes ?? []);
    setSystemId(t.systemId ?? '');
    setBlocks((t.blocks ?? []).map((b) => ({ key: newKey(), blockId: b.blockId, name: b.name, multiplier: String(b.multiplier) })));
    setLines((t.lines ?? []).map(toDraft));
    setChecklist((t.checklist ?? []).map((text) => ({ key: newKey(), text })));
  };

  const wire = () => ({
    name,
    notes,
    scopes,
    systemId: systemId === '' ? null : systemId,
    blocks: blocks.map((b) => ({ blockId: b.blockId, multiplier: Number(b.multiplier) })),
    lines: toWire(lines),
    checklist: checklist.map((c) => c.text.trim()).filter((t) => t !== ''),
  });

  const save = useMutation({
    mutationFn: () => {
      const next = wire();
      if (!base) return api<Template>('/api/templates/templates', { method: 'POST', body: next });
      const before: Record<string, unknown> = {
        name: base.name,
        notes: base.notes,
        scopes: base.scopes,
        systemId: base.systemId,
        blocks: base.blocks?.map((b) => ({ blockId: b.blockId, multiplier: b.multiplier })),
        lines: base.lines && toWire(base.lines.map(toDraft)),
        checklist: base.checklist,
      };
      // only the visible fields that changed (rule 7a)
      const changed = Object.fromEntries(
        Object.entries(next).filter(([k, v]) => sees(k as keyof Template) && JSON.stringify(v) !== JSON.stringify(before[k])),
      );
      return api<Template>(`/api/templates/templates/${base.id}`, { method: 'PATCH', body: { version: base.version, ...changed } });
    },
    onSuccess: async (t) => {
      load(t);
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
  const error = save.error instanceof ApiError ? save.error : null;
  const conflict = error?.status === 409;

  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    if (!validQuantities(lines) || !blocks.every((b) => /^\d+$/.test(b.multiplier.trim()) && Number(b.multiplier) > 0 && Number(b.multiplier) <= 1000)) {
      setLocalError('Ilość w każdym wierszu i krotność każdego bloku muszą być liczbą całkowitą większą od zera.');
      return;
    }
    setLocalError('');
    save.mutate();
  }

  async function searchBlocks(q: string, signal: AbortSignal): Promise<BlockOption[]> {
    const res = await api<{ blocks: BlockSummary[] }>(`/api/templates/blocks?q=${encodeURIComponent(q)}`, { signal });
    return res.blocks.slice(0, 15).map((b) => ({ key: b.id, name: b.name, details: [`pozycji: ${b.lineCount}`], block: b }));
  }
  function pickBlock(option: BlockOption, quantity: number) {
    const same = blocks.find((b) => b.blockId === option.block.id);
    if (same) setBlocks(blocks.map((b) => (b === same ? { ...b, multiplier: String((Number(b.multiplier) || 0) + quantity) } : b)));
    else setBlocks([...blocks, { key: newKey(), blockId: option.block.id, name: option.block.name, multiplier: String(quantity) }]);
  }
  function moveCheck(i: number, by: -1 | 1) {
    const next = [...checklist];
    const [item] = next.splice(i, 1);
    next.splice(i + by, 0, item!);
    setChecklist(next);
  }
  function addCheck() {
    if (newCheck.trim() === '') return;
    setChecklist([...checklist, { key: newKey(), text: newCheck.trim() }]);
    setNewCheck('');
  }

  const span = sheetSpan(canEdit);
  const blocksSection = sees('blocks') && (
    <SheetSection name="Bloki">
      {blocks.map((b, i) => (
        <tr key={b.key} className="sheet-row tpl-block-row">
          <th scope="row" className="sheet-section-cell">
            {i === 0 && <span className="sheet-section-name">Bloki</span>}
          </th>
          <td className="sheet-main">
            {b.name} <span className="chip">blok</span>
          </td>
          <td className="num tpl-qty">
            {canEdit ? (
              <CellInput numeric inputMode="numeric" aria-label={`Krotność: ${b.name}`} value={b.multiplier} onChange={(e) => setBlocks(blocks.map((x) => (x.key === b.key ? { ...x, multiplier: e.target.value } : x)))} />
            ) : (
              `×${b.multiplier}`
            )}
          </td>
          {canEdit && (
            <td className="q-actions">
              <button className="btn icon ghost q-remove" type="button" aria-label={`Usuń blok: ${b.name}`} title="Usuń blok" onClick={() => setBlocks(blocks.filter((x) => x.key !== b.key))}>
                ×
              </button>
            </td>
          )}
        </tr>
      ))}
      {blocks.length === 0 && (
        <tr className="sheet-empty">
          <th scope="row" className="sheet-section-cell">
            <span className="sheet-section-name">Bloki</span>
          </th>
          <td colSpan={span} className="muted">
            Szablon nie ma bloków.
          </td>
        </tr>
      )}
      {canEdit && (
        <SheetAddRow span={span}>
          <QuickPicker<BlockOption> label="Dodaj blok" labelHidden placeholder="Dodaj blok, np. mikrofony, 2x okablowanie" search={searchBlocks} onPick={pickBlock} featureId="templates.templates" />
        </SheetAddRow>
      )}
    </SheetSection>
  );
  // the compact line above the table: what the settings under ⚙ hold
  const settingsSummary = [
    sees('scopes') ? `Zakres: ${scopes.length > 0 ? scopes.map((k) => EVENT_SCOPES[k as keyof typeof EVENT_SCOPES] ?? k).join(', ') : '—'}` : '',
    sees('systemId') ? `System: ${systemId ? systemLabel(systems, systemId) : '—'}` : '',
    sees('checklist') ? `Lista kontrolna: ${checklist.length > 0 ? `${checklist.length} ${checklist.length === 1 ? 'punkt' : checklist.length < 5 ? 'punkty' : 'punktów'}` : '—'}` : '',
    sees('notes') && notes.trim() ? 'z opisem' : '',
  ].filter(Boolean);

  // owner 2026-10-23 21:14: "Zamknij", another one or another screen with unsaved changes asks first
  const plain = (ls: DraftLine[]) => ls.map(({ key: _key, ...l }) => ({ ...l, quantity: l.quantity.trim() }));
  const now = {
    name,
    notes,
    scopes: [...scopes].sort(),
    systemId,
    blocks: blocks.map((b) => ({ blockId: b.blockId, multiplier: b.multiplier.trim() })),
    lines: plain(lines),
    checklist: checklist.map((c) => c.text.trim()).filter((t) => t !== ''),
    newCheck: newCheck.trim(),
  };
  const was = {
    name: base?.name ?? '',
    notes: base?.notes ?? '',
    scopes: [...(base?.scopes ?? [])].sort(),
    systemId: base?.systemId ?? '',
    blocks: (base?.blocks ?? []).map((b) => ({ blockId: b.blockId, multiplier: String(b.multiplier) })),
    lines: plain((base?.lines ?? []).map(toDraft)),
    checklist: base?.checklist ?? [],
    newCheck: '',
  };
  const guardRef = useUnsavedGuard({
    dirty: canEdit && !sameValues(now, was),
    save: async (el) => {
      if (!validQuantities(lines) || !blocks.every((b) => /^\d+$/.test(b.multiplier.trim()) && Number(b.multiplier) > 0 && Number(b.multiplier) <= 1000)) {
        setLocalError('Ilość w każdym wierszu i krotność każdego bloku muszą być liczbą całkowitą większą od zera.');
        return false;
      }
      if (!validForm(el)) return false;
      await save.mutateAsync();
      return true;
    },
  });
  return (
    <form ref={guardRef} className="panel form wide tpl-editor" aria-label={base ? `Szablon: ${base.name}` : 'Nowy szablon'} data-feature-id="templates.templates" onSubmit={submit}>
      {conflict && (
        <ConflictNotice
          onReload={() => {
            const current = error.current as Template | undefined;
            void (current ? Promise.resolve(current) : api<Template>(`/api/templates/templates/${base!.id}`)).then(load);
            save.reset();
          }}
        />
      )}
      {error && !conflict && (
        <p className="alert" role="alert">
          {error.message}
          {fieldIssues(error).map((i) => (
            <span key={i.path + i.message}> {i.message}</span>
          ))}
        </p>
      )}
      {localError && (
        <p className="alert" role="alert">
          {localError}
        </p>
      )}
      {saved && !error && (
        <Notice onClose={() => setSaved(false)}>
          Zapisano.
        </Notice>
      )}
      <h2>{base ? `Szablon: ${base.name}` : 'Nowy szablon'}</h2>
      <label className="field tpl-name">
        <span>Nazwa</span>
        <input value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" readOnly={!canEdit} placeholder="np. Koncert plenerowy standard" />
      </label>
      <div className="tpl-settings-bar">
        <span className="muted">{settingsSummary.join(' · ')}</span>
        <button className="btn icon ghost tpl-gear" type="button" aria-label="Ustawienia szablonu" aria-expanded={showSettings} title="Ustawienia szablonu: zakres prac, system, lista kontrolna, opis" onClick={() => setShowSettings(!showSettings)}>
          ⚙
        </button>
      </div>
      {showSettings && (
        <div className="tpl-settings" role="group" aria-label="Ustawienia szablonu">
          {sees('scopes') && (
            <fieldset className="choices inline">
              <legend>Zakres prac</legend>
              {scopeKeys.map((s) => (
                <label key={s} className="check">
                  <input
                    type="checkbox"
                    checked={scopes.includes(s)}
                    disabled={!canEdit}
                    onChange={(e) => setScopes(e.target.checked ? scopeKeys.filter((k) => k === s || scopes.includes(k)) : scopes.filter((k) => k !== s))}
                  />
                  {EVENT_SCOPES[s]}
                </label>
              ))}
            </fieldset>
          )}
          {sees('systemId') && (
            <label className="field">
              <span>System</span>
              <select value={systemId} onChange={(e) => setSystemId(e.target.value)} disabled={!canEdit}>
                <option value="">— nie ustalono —</option>
                {systemGroups(systems, systemId).flatMap((g) => g.items).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.deleted ? `${s.name} (usunięty)` : s.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {sees('checklist') && (
            <section aria-label="Lista kontrolna" className="tpl-checklist">
              <h3>Lista kontrolna</h3>
              {checklist.length === 0 && <p className="muted">Brak punktów.</p>}
              <ol className="order-list">
                {checklist.map((c, i) => (
                  <li key={c.key} className="order-row">
                    <label className="field">
                      <span className="visually-hidden">Punkt {i + 1}</span>
                      <input value={c.text} readOnly={!canEdit} onChange={(e) => setChecklist(checklist.map((x) => (x.key === c.key ? { ...x, text: e.target.value } : x)))} />
                    </label>
                    {canEdit && (
                      <span className="actions">
                        <button className="btn small" type="button" aria-label={`W górę: ${c.text}`} disabled={i === 0} onClick={() => moveCheck(i, -1)}>
                          ↑
                        </button>
                        <button className="btn small" type="button" aria-label={`W dół: ${c.text}`} disabled={i === checklist.length - 1} onClick={() => moveCheck(i, 1)}>
                          ↓
                        </button>
                        <button className="btn small" type="button" aria-label={`Usuń punkt: ${c.text}`} onClick={() => setChecklist(checklist.filter((x) => x.key !== c.key))}>
                          Usuń
                        </button>
                      </span>
                    )}
                  </li>
                ))}
              </ol>
              {canEdit && (
                <div className="field-row">
                  <label className="field">
                    <span>Nowy punkt</span>
                    <input
                      value={newCheck}
                      placeholder="np. Sprawdzić prąd na scenie"
                      onChange={(e) => setNewCheck(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        addCheck();
                      }}
                    />
                  </label>
                  <button className="btn small" type="button" onClick={addCheck}>
                    Dodaj punkt
                  </button>
                </div>
              )}
            </section>
          )}
          {sees('notes') && (
            <label className="field">
              <span>Opis</span>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} readOnly={!canEdit} />
            </label>
          )}
        </div>
      )}

      {sees('lines') ? (
        <LinesSheet lines={lines} onChange={setLines} canEdit={canEdit} label="Dodaj do szablonu" featureId="templates.templates" leading={blocksSection} {...(sees('blocks') ? { summary: `bloków: ${blocks.length}` } : {})} />
      ) : (
        sees('blocks') && (
          <div className="table-wrap">
            <table className="table sheet-table tpl-table" aria-label="Bloki">
              {blocksSection}
            </table>
          </div>
        )
      )}

      <div className="actions">
        {canEdit && (
          <button className="btn primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Zapisywanie…' : base ? 'Zapisz zmiany' : 'Dodaj szablon'}
          </button>
        )}
        <button className="btn" type="button" onClick={(e) => leaveGuarded(onClose, e.currentTarget.closest('form'))}>
          Zamknij
        </button>
      </div>
    </form>
  );
}

/** Add (no `templateId`) or edit a template. */
export function TemplateEditor({ templateId, onClose }: { templateId?: string; onClose: () => void }) {
  const detail = useQuery({ queryKey: [...TEMPLATES_KEY, 'template', templateId], enabled: templateId !== undefined, gcTime: 0, queryFn: () => api<Template>(`/api/templates/templates/${templateId}`) });
  if (templateId === undefined) return <TemplateForm onClose={onClose} />;
  if (detail.error) {
    return (
      <p className="alert" role="alert">
        {detail.error instanceof ApiError ? detail.error.message : 'Nie udało się wczytać szablonu'}
      </p>
    );
  }
  if (!detail.data) return <p className="muted" role="status">Wczytywanie…</p>;
  return <TemplateForm key={detail.data.id} initial={detail.data} onClose={onClose} />;
}
